import React from 'react';
import { Helmet } from 'react-helmet-async';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Users, Clock, Flame, Sparkles, CheckCircle2, Phone, Mail, Calendar } from 'lucide-react';

export default function SaunaRental() {
  const highlights = [
    {
      icon: Clock,
      title: '1,5 times varighed',
      desc: 'Nyd 90 minutters uforstyrret varme, ro og afslapning.',
    },
    {
      icon: Users,
      title: 'Plads til 6-8 personer',
      desc: 'Perfekt til vennegrupper, familiehygge eller en hyggelig komsammen.',
    },
    {
      icon: Flame,
      title: '1.500 kr. for hele saunaen',
      desc: 'Fast pris for leje af hele saunaen – I har det hele for jer selv.',
    },
  ];

  const includes = [
    'Eksklusiv privat adgang til hele saunaen',
    'Plads og siddekomfort til 6-8 personer',
    'Forvarmet og klar til brug ved ankomst',
    'Mulighed for saunagus / æteriske olier efter aftale',
    'Rolige og private omgivelser',
    'Adgang til isbad & brus',
  ];

  return (
    <div className="bg-stone-50 min-h-screen">
      <Helmet>
        <title>Sauna Udlejning | Privat leje til 6-8 personer</title>
        <meta
          name="description"
          content="Lej hele saunaen i 1,5 timer til 1.500 kr. Plads til 6-8 personer. Nyd en eksklusiv og afslappende saunaoplevelse i rolige omgivelser."
        />
      </Helmet>

      {/* Hero Section */}
      <section className="relative h-[60vh] min-h-[460px] flex items-center justify-center overflow-hidden">
        <div className="absolute inset-0">
          <img
            src="/_assets/sauna-udlejning-hero.jpg"
            alt="Sauna udlejning"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-stone-900/60 backdrop-blur-[1px]" />
        </div>

        <div className="relative max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center text-white">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
          >
            <span className="inline-block text-xs uppercase tracking-[0.4em] font-bold text-stone-300 mb-4">
              Eksklusiv Velvære &amp; Ro
            </span>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-light tracking-wide mb-6">
              Sauna Udlejning
            </h1>
            <p className="text-lg sm:text-xl text-stone-200 font-light max-w-2xl mx-auto leading-relaxed">
              Lej hele saunaen til jeres eget private selskab – 1,5 times ren forkælelse og afslapning.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Key Pricing & Features Bar */}
      <section className="relative -mt-12 z-10 max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="bg-white shadow-xl border border-stone-200/80 p-8 sm:p-10 grid grid-cols-1 md:grid-cols-3 gap-8 text-center">
          {highlights.map((item, idx) => {
            const Icon = item.icon;
            return (
              <div key={idx} className="flex flex-col items-center">
                <div className="w-14 h-14 rounded-full bg-stone-100 flex items-center justify-center text-stone-800 mb-4">
                  <Icon className="w-7 h-7" />
                </div>
                <h3 className="text-lg font-medium text-stone-900 mb-2">{item.title}</h3>
                <p className="text-sm text-stone-600 leading-relaxed">{item.desc}</p>
              </div>
            );
          })}
        </div>
      </section>

      {/* Details Section */}
      <section className="py-24 max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
          <div>
            <span className="text-xs uppercase tracking-[0.3em] font-bold text-stone-500 block mb-3">
              Privat saunaoplevelse
            </span>
            <h2 className="text-3xl sm:text-4xl font-light text-stone-900 mb-6 leading-tight">
              Koble af i varme og stemningsfulde omgivelser
            </h2>
            <p className="text-stone-600 leading-relaxed mb-6">
              Skal I fejre en fødselsdag, holde en hyggelig venindeaften, eller ønsker I blot en fælles pause fra en travl hverdag? Vores sauna giver jer de perfekte rammer for en rolig stund med velvære i fokus.
            </p>
            <p className="text-stone-600 leading-relaxed mb-8">
              Når I lejer saunaen, er det udelukkende jer, der har adgang. Saunaen er varmet godt op, så I kan træde direkte ind i den behagelige varme fra første minut.
            </p>

            <div className="bg-stone-100 p-6 border-l-4 border-stone-800 mb-8">
              <div className="text-2xl font-light text-stone-900 mb-1">
                1.500 kr. <span className="text-sm font-normal text-stone-600">/ 1,5 time</span>
              </div>
              <p className="text-sm text-stone-600">
                Gælder for hele saunaen med plads til op til 6-8 personer.
              </p>
            </div>

            <div className="space-y-3 mb-10">
              {includes.map((point, i) => (
                <div key={i} className="flex items-center gap-3 text-stone-700">
                  <CheckCircle2 className="w-5 h-5 text-stone-800 shrink-0" />
                  <span className="text-sm">{point}</span>
                </div>
              ))}
            </div>

            <div className="flex flex-wrap gap-4">
              <Link
                to="/kontakt"
                className="bg-stone-800 text-white px-8 py-4 text-xs uppercase tracking-widest font-bold hover:bg-stone-900 transition-colors"
              >
                Book eller forespørg tid
              </Link>
              <a
                href="tel:+4528550499"
                className="border border-stone-400 text-stone-800 px-8 py-4 text-xs uppercase tracking-widest font-bold hover:bg-stone-100 transition-colors inline-flex items-center gap-2"
              >
                <Phone className="w-4 h-4" />
                Ring 28 55 04 99
              </a>
            </div>
          </div>

          <div className="relative">
            <div className="overflow-hidden shadow-2xl">
              <img
                src="/_assets/sauna-interioer.jpg"
                alt="Sauna interiør med træbænke og ovn"
                className="w-full h-[520px] object-cover hover:scale-105 transition-transform duration-700"
              />
            </div>
            <div className="absolute -bottom-6 -left-6 bg-stone-900 text-white p-6 shadow-xl hidden sm:block max-w-xs">
              <div className="flex items-center gap-2 text-stone-300 text-xs uppercase tracking-wider mb-2">
                <Sparkles className="w-4 h-4" />
                <span>Total velvære</span>
              </div>
              <p className="text-xs text-stone-300 leading-relaxed">
                6-8 personer i 1,5 time for 1.500 kr. – afslapning og hygge i særklasse.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Practical Information */}
      <section className="bg-white py-20 border-t border-stone-200">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <span className="text-xs uppercase tracking-[0.3em] font-bold text-stone-500 block mb-2">
              Praktisk info
            </span>
            <h2 className="text-3xl font-light text-stone-900">Godt at vide inden jeres besøg</h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 text-stone-600 text-sm">
            <div className="border border-stone-200 p-6">
              <h3 className="text-base font-semibold text-stone-900 mb-3 flex items-center gap-2">
                <Calendar className="w-5 h-5 text-stone-700" />
                Booking &amp; tidsbestilling
              </h3>
              <p className="leading-relaxed">
                Udlejning sker efter forudgående aftale, så vi sikrer, at saunaen er fuldt opvarmet og klar til jer. Kontakt os gerne i god tid for at sikre jeres ønskede tidspunkt.
              </p>
            </div>

            <div className="border border-stone-200 p-6">
              <h3 className="text-base font-semibold text-stone-900 mb-3 flex items-center gap-2">
                <Users className="w-5 h-5 text-stone-700" />
                Hvad skal I medbringe?
              </h3>
              <p className="leading-relaxed">
                Medbring gerne eget badetøj, badesandaler og to håndklæder pr. person (et til at sidde på i saunaen og et til efter bad/afkøling). Drikkedunk anbefales altid.
              </p>
            </div>
          </div>

          {/* CTA Box */}
          <div className="mt-16 bg-stone-100 p-8 sm:p-10 text-center">
            <h3 className="text-2xl font-light text-stone-900 mb-3">Klar til at reservere saunaen?</h3>
            <p className="text-stone-600 max-w-xl mx-auto mb-8 text-sm leading-relaxed">
              Kontakt os direkte på telefon eller via kontaktformularen, så finder vi et tidspunkt, der passer jer perfekt.
            </p>
            <div className="flex flex-wrap justify-center gap-4">
              <Link
                to="/kontakt"
                className="bg-stone-800 text-white px-8 py-3.5 text-xs uppercase tracking-widest font-bold hover:bg-stone-900 transition-colors flex items-center gap-2"
              >
                <Mail className="w-4 h-4" />
                Gå til kontakt
              </Link>
              <a
                href="tel:+4528550499"
                className="bg-white border border-stone-300 text-stone-800 px-8 py-3.5 text-xs uppercase tracking-widest font-bold hover:bg-stone-50 transition-colors flex items-center gap-2"
              >
                <Phone className="w-4 h-4" />
                Ring: 28 55 04 99
              </a>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
